import {
  endOfToday,
  endOfWeek,
  endOfYesterday,
  isEqual,
  startOfMonth,
  startOfToday,
  startOfWeek,
  startOfYear,
  startOfYesterday,
} from 'date-fns';
import { getKeyValues } from 'src/util/object';

export interface DateRange {
  startDate: Date | undefined;
  endDate: Date | undefined;
}

export function areEqual(range1: DateRange, range2: DateRange) {
  if (range1 != null && range2 != null) {
    // tslint:disable-next-line: no-non-null-assertion
    const startEqual = isEqual(range1.startDate!, range2.startDate!);
    // tslint:disable-next-line: no-non-null-assertion
    const endEqual = isEqual(range1.endDate!, range2.endDate!);
    return startEqual && endEqual;
  }
  return range1 == null && range2 == null;
}

export function today(): DateRange {
  return {
    startDate: startOfToday(),
    endDate: endOfToday(),
  };
}

export function yesterday(): DateRange {
  return {
    startDate: startOfYesterday(),
    endDate: endOfYesterday(),
  };
}

export function thisWeek(): DateRange {
  const _today = new Date();
  return {
    startDate: startOfWeek(_today),
    endDate: endOfWeek(_today),
  };
}

export function monthToDate(): DateRange {
  return {
    startDate: startOfMonth(new Date()),
    endDate: endOfToday(),
  };
}

export function yearToDate(): DateRange {
  return {
    startDate: startOfYear(new Date()),
    endDate: endOfToday(),
  };
}

export interface DateRangeChangeEvent {
  rangeKey: string | null;
  range: DateRange;
}

export interface DateRangeOption {
  key: string;
  label: string;
  value: DateRange;
}

export interface DateRangeOptionParams {
  label: string;
  rangeCreator: () => DateRange;
}

export function createDateRangeOption(optionParams: {
  key: string;
  value: DateRangeOptionParams;
}): DateRangeOption {
  return {
    key: optionParams.key,
    label: optionParams.value.label,
    value: optionParams.value.rangeCreator(),
  };
}

export const dateRangeOptionCreators: {
  [key: string]: DateRangeOptionParams;
} = {
  // Keys here are pascal case as they are used as values
  // and may be visible to the user
  Today: { label: 'Today', rangeCreator: today },
  Yesterday: { label: 'Yesterday', rangeCreator: yesterday },
  ThisWeek: { label: 'This Week', rangeCreator: thisWeek },
  MonthToDate: { label: 'Month to Date', rangeCreator: monthToDate },
  YearToDate: { label: 'Year to Date', rangeCreator: yearToDate },
};

export const getDateRangeOptions = () =>
  getKeyValues(dateRangeOptionCreators).map(createDateRangeOption);
